import React, { useCallback, useEffect, useRef } from 'react';

const WebDocAutoGrowTextarea: React.FC<{
  value: string;
  onChange: (value: string) => void;
  className?: string;
  shellClassName?: string;
  fillAvailableHeight?: boolean;
  placeholder?: string;
  ariaLabel: string;
}> = ({
  value,
  onChange,
  className = '',
  shellClassName = '',
  fillAvailableHeight = false,
  placeholder,
  ariaLabel,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const syncHeight = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    el.style.height = 'auto';
    const contentHeight = el.scrollHeight;
    const shellHeight = shellRef.current?.clientHeight ?? el.parentElement?.clientHeight ?? 0;
    const availableHeight = fillAvailableHeight ? shellHeight : 0;

    if (fillAvailableHeight && availableHeight > 0) {
      const heightPx = `${availableHeight}px`;
      const overflowY = contentHeight > availableHeight ? 'auto' : 'hidden';

      if (
        el.style.minHeight === heightPx &&
        el.style.maxHeight === heightPx &&
        el.style.height === heightPx &&
        el.style.overflowY === overflowY
      ) {
        return;
      }

      el.style.minHeight = heightPx;
      el.style.maxHeight = heightPx;
      el.style.height = heightPx;
      el.style.overflowY = overflowY;
      return;
    }

    const floorHeight = shellHeight > 0 ? shellHeight : 44;
    const targetHeight = Math.max(contentHeight, floorHeight);
    const heightPx = `${targetHeight}px`;

    if (el.style.height === heightPx && el.style.overflowY === 'hidden' && !el.style.minHeight && !el.style.maxHeight) {
      return;
    }

    el.style.minHeight = '';
    el.style.maxHeight = '';
    el.style.height = heightPx;
    el.style.overflowY = 'hidden';
  }, [fillAvailableHeight]);

  const scheduleSyncHeight = useCallback(() => {
    window.requestAnimationFrame(() => {
      syncHeight();
    });
  }, [syncHeight]);

  useEffect(() => {
    scheduleSyncHeight();
  }, [value, scheduleSyncHeight, fillAvailableHeight]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        syncHeight();
      });
    });
    observer.observe(shell);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [syncHeight]);

  const focusTextarea = () => {
    ref.current?.focus();
  };

  return (
    <div
      ref={shellRef}
      className={`cp-evaluation-plan-web-doc-cell-input-shell${shellClassName ? ` ${shellClassName}` : ''}`}
      onMouseDown={(event) => {
        if (event.target === ref.current) return;
        event.preventDefault();
        focusTextarea();
      }}
    >
      <textarea
        ref={ref}
        className={className}
        value={value}
        rows={1}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value)}
        onInput={scheduleSyncHeight}
      />
    </div>
  );
};

export default WebDocAutoGrowTextarea;
